import React, { useState, useEffect } from "react";

import {
  CardList,
  CardListItem,
  Checkbox,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  InfoPanel,
  ListPanel,
  SelectableListItem,
  Select,
  SelectData,
  useStepperDispatch,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import {
  getInstitutionDisplayNameAndTown,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getIsGroup } from "../../../api/kindergartenApi";
import { RootState } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";
import { useGetGroupInstitutions } from "../../../hooks/codebook/useGetGroupInstitutions";
import { useGetInstitutionProgramAges } from "../../../hooks/codebook/useGetProgramAges";
import { useGetInstitutionProgramDurations } from "../../../hooks/codebook/useGetProgramDurations";
import { useGetInstitutionProgramsFilter } from "../../../hooks/codebook/useGetPrograms";
import useInput from "../../../hooks/input/useInput";
import InstitutionProgram from "../../../models/Institution/InstitutionProgram";
import InstitutionProgramListItem from "../../../models/Institution/InstitutionProgramListItem";
import { RequestInstitutionProgramFilters } from "../../../models/Institution/InstitutionProgramSearch";
import InstitutionProgramValidationSchema from "../../../models/Institution/InstitutionProgramValidationSchema";
import { WorkOrganizationEnum } from "../../../models/Institution/WorkOrganizationEnum";
import CodeBook from "../../../models/Shared/CodeBook";
import { getLocationPrograms } from "../../../slices/institutionSlice";
import {
  getCriteria,
  updateRequestInstitutionPrograms,
} from "../../../slices/requestSlice";
import styles from "./InstitutionProgramDataStep.module.scss";

export default function InstitutionProgramDataStep() {
  const emptyValue = "-";
  const maxPrograms = 4;

  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number>();

  const [locationPrograms, setLocationPrograms] = useState<
    InstitutionProgramListItem[]
  >([]);

  const institutionProgramsState = useSelector(
    (state: RootState) => state.request.request.institutionPrograms
  );

  const [institutionPrograms, setInstitutionPrograms] = useState<
    InstitutionProgram[]
  >(institutionProgramsState);

  const pedagogicalYearInstitutionId = useSelector(
    (state: RootState) =>
      state.request.request.selectedInstitution.pedagogicalYearInstitutionId
  );

  const requestType = useSelector(
    (state: RootState) => state.request.request.requestTypeId
  );

  const programAges = useGetInstitutionProgramAges(
    pedagogicalYearInstitutionId
  );

  const programDurations = useGetInstitutionProgramDurations(
    pedagogicalYearInstitutionId
  );

  const groupInstitutions = useGetGroupInstitutions(
    pedagogicalYearInstitutionId
  );

  const [isGroup, setIsGroup] = useState(false);

  const [filters, setFilters] = useState<RequestInstitutionProgramFilters>({
    requestTypeId: requestType,
  } as RequestInstitutionProgramFilters);

  const programs = useGetInstitutionProgramsFilter(
    pedagogicalYearInstitutionId,
    filters.parentIntitutionId
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { handleChange } = useInput();

  useEffect(() => {
    getIsGroup(pedagogicalYearInstitutionId).then((res) => {
      setIsGroup(res);
    });
  }, [pedagogicalYearInstitutionId]);

  useEffect(() => {
    const filterParams = {
      pedagogicalYearInstitutionId: pedagogicalYearInstitutionId,
      parentIntitutionId: filters.parentIntitutionId,
      programAgeId: filters.programAgeId,
      programDurationId: filters.programDurationId,
      programId: filters.programId,
      requestTypeId: filters.requestTypeId,
    };
    dispatch(getLocationPrograms(filterParams))
      .then(unwrapResult)
      .then((res) => {
        setLocationPrograms(res);
      });
    dispatch(getCriteria());
  }, [
    dispatch,
    pedagogicalYearInstitutionId,
    filters.parentIntitutionId,
    filters.programAgeId,
    filters.programDurationId,
    filters.programId,
    filters.requestTypeId,
  ]);

  const handleListItemClick = (e: any) => {
    const { value, isSelected } = e.target;
    var canSelect = locationPrograms.find((x) => x.institutionId === value)
      ?.canPickedOnFirstPlace;
    var isEmptyPickedInstitutionPrograms = institutionPrograms.length === 0;

    if (isSelected) {
      if (isGroup) {
        if (canSelect || !isEmptyPickedInstitutionPrograms) {
          setSelectedInstitutionId(value);
        } else {
          setSelectedInstitutionId(0);
        }
      } else {
        setSelectedInstitutionId(value);
      }
    } else {
      setSelectedInstitutionId(undefined);
    }
  };

  const [errors, doValidations] = useValidation({
    data: institutionPrograms,
    validationSchema: InstitutionProgramValidationSchema,
  });

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "save",
    payload: () => {
      dispatch(updateRequestInstitutionPrograms(institutionPrograms));
    },
  });

  const handleStep = () => {
    if (institutionPrograms) {
      const isValid = doValidations();
      if (isValid) {
        dispatch(updateRequestInstitutionPrograms(institutionPrograms));
      } else {
        return isValid;
      }
    } else {
      return false;
    }
  };

  const handleProgramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (checked && institutionPrograms.length < maxPrograms) {
      const locationProgram = locationPrograms.find(
        (lp) => lp.pedagogicalYearInstitutionProgramDetailId === +value
      );

      setInstitutionPrograms((prev) => [
        ...prev,
        {
          pedagogicalYearInstitutionProgramDetailId: +value,
          priority: prev.length + 1,
          locationName: locationProgram?.institutionName,
          locationTown: locationProgram?.institutionTown,
          programName: locationProgram?.programName,
          canPickedOnFirstPlace: !isGroup
            ? true
            : locationProgram
            ? locationProgram.canPickedOnFirstPlace
            : true,
        },
      ]);
    } else {
      let newInstitutionPrograms = institutionPrograms.filter(
        (ip) => ip.pedagogicalYearInstitutionProgramDetailId !== +value
      );

      newInstitutionPrograms = newInstitutionPrograms.map((program, index) => {
        return { ...program, priority: index + 1 };
      });

      setInstitutionPrograms(newInstitutionPrograms);
    }
  };

  const handleDrag = (sourceIndex: number, destinationIndex: number) => {
    const itemOnIndex = institutionPrograms[sourceIndex];
    let newInstitutionPrograms = [...institutionPrograms];

    //remove item from old index
    newInstitutionPrograms.splice(sourceIndex, 1);

    //add item to new index
    newInstitutionPrograms.splice(destinationIndex, 0, itemOnIndex);

    newInstitutionPrograms = newInstitutionPrograms.map((program, index) => {
      return { ...program, priority: index + 1 };
    });

    setInstitutionPrograms(newInstitutionPrograms);
  };

  const handleClose = (closedItemIndex: number) => {
    let newInstitutionPrograms = [...institutionPrograms];
    //remove item
    newInstitutionPrograms.splice(closedItemIndex, 1);
    setSelectedInstitutionId(undefined);

    newInstitutionPrograms = newInstitutionPrograms.map((program, index) => {
      return { ...program, priority: index + 1 };
    });

    setInstitutionPrograms(newInstitutionPrograms);
  };

  const isProgramSelected = (
    pedagogicalYearInstitutionProgramDetailId: number
  ) => {
    return (
      institutionPrograms.find(
        (i) =>
          i.pedagogicalYearInstitutionProgramDetailId ===
          pedagogicalYearInstitutionProgramDetailId
      ) != null
    );
  };

  const getInstitutions = () => {
    if (locationPrograms && locationPrograms.length > 0) {
      let institutions = [] as InstitutionProgramListItem[];
      locationPrograms.forEach((institution) => {
        if (
          institutions.length === 0 ||
          institutions.find(
            (i) => i.institutionId === institution.institutionId
          ) == null
        ) {
          institutions.push(institution);
        }
      });

      return institutions.map((institution) => {
        return (
          <SelectableListItem
            key={institution.pedagogicalYearInstitutionProgramDetailId}
            text={getInstitutionDisplayNameAndTown(
              institution.institutionName,
              institution.institutionTown
            )}
            helperText={`${institution.institutionAddress}, ${institution.institutionTown}`}
            value={institution.institutionId}
            onClick={handleListItemClick}
          />
        );
      });
    }
  };

  const getPrograms = () => {
    if (
      locationPrograms &&
      locationPrograms.length > 0 &&
      selectedInstitutionId
    ) {
      if (selectedInstitutionId === 0) {
        return null;
      } else {
        const programs = locationPrograms.filter(
          (ip) => ip.institutionId === selectedInstitutionId
        );
        return programs.map((program) => {
          return (
            <div
              className={styles.listItem}
              key={program.pedagogicalYearInstitutionProgramDetailId}
            >
              <ExpandablePanel>
                <ExpandablePanelHeader className={styles.listItemHeader} small>
                  <Checkbox
                    label={`${program.programName}`}
                    helperText={`${program.programDuration}, ${program.programLanguage}`}
                    checked={isProgramSelected(
                      program.pedagogicalYearInstitutionProgramDetailId
                    )}
                    value={program.pedagogicalYearInstitutionProgramDetailId}
                    onChange={handleProgramChange}
                  />
                </ExpandablePanelHeader>
                <ExpandablePanelBody className={styles.listItemBody}>
                  <TextField
                    label={t("institutionProgram.address")}
                    value={getInstitutionDisplayNameAndTown(
                      program.institutionAddress,
                      program.institutionTown
                    )}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    type="email"
                    label={t("institutionProgram.email")}
                    value={program.email}
                    color="primary"
                    plaintext
                    smallLabel
                  />
                  <TextField
                    type="url"
                    label={t("institutionProgram.webAddress")}
                    value={program.webAddress}
                    color="primary"
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("institutionProgram.workOrganization")}
                    value={program.workOrganizationName}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("institutionProgram.capacity")}
                    value={program.capacity}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("institutionProgram.economyPrice")}
                    value={t("institutionDetails.EurAndHrk", {
                      priceEUR: program?.priceEUR.toFixed(2),
                      priceHRK: program?.priceHRK.toFixed(2),
                    }).toString()}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("common.programDuration")}
                    value={`${t("institutionProgram.duration", {
                      duration: program.duration,
                    })}`}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("institutionProgram.firstShift")}
                    value={`${program.firstShiftFrom} - ${program.firstShiftTo}`}
                    plaintext
                    smallLabel
                  />
                  {program.workOrganizationId ===
                    WorkOrganizationEnum.Shifts && (
                    <TextField
                      label={t("institutionProgram.secondShift")}
                      value={`${program.secondShiftFrom} - ${program.secondShiftTo}`}
                      plaintext
                      smallLabel
                    />
                  )}
                  <TextField
                    label={t("institutionProgram.description")}
                    value={program.programDescription || emptyValue}
                    plaintext
                    smallLabel
                  />
                  <TextField
                    label={t("institutionProgram.note")}
                    value={program.programNote || emptyValue}
                    plaintext
                    smallLabel
                  />

                  <Row>
                    <Col md={12}>
                      <InfoPanel
                        message={t("institutionDetails.exchangeRate", {
                          exchangeRate: program?.exchangeRate,
                        })}
                        className={styles.zeroPadding}
                        color="warning"
                      />
                    </Col>
                  </Row>
                </ExpandablePanelBody>
              </ExpandablePanel>
            </div>
          );
        });
      }
    }
  };

  const getSelectedPrograms = () => {
    if (institutionPrograms.length > 0) {
      return institutionPrograms.map((institutionProgram) => {
        if (institutionProgram) {
          return (
            <CardListItem
              key={institutionProgram.pedagogicalYearInstitutionProgramDetailId}
              itemKey={institutionProgram.pedagogicalYearInstitutionProgramDetailId.toString()}
              color="grey"
              badgeColor="grey"
              badgeSize="medium"
              badgeOutline
            >
              <div>
                <Typography
                  variant="largeBold"
                  className={styles.institutionNamePriority}
                >
                  {getInstitutionDisplayNameAndTown(
                    institutionProgram.locationName!,
                    institutionProgram.locationTown!
                  )}
                </Typography>
                <Typography
                  variant="medium"
                  className={styles.programNamePriority}
                >
                  {institutionProgram.programName}
                </Typography>
              </div>
            </CardListItem>
          );
        } else {
          return <></>;
        }
      });
    } else {
      return <></>;
    }
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <InfoPanel
            message={t("institutionProgramDataStep.mainInfoMessage")}
            color="success"
            className={styles.mainInfoMessage}
            fullWidth
          />
        </Col>

        {isGroup && (
          <Col md={12}>
            <InfoPanel
              message={t("validations.institutionProgramFirstPickWarning")}
              color="warning"
              className={styles.mainInfoMessage}
              fullWidth
            />
          </Col>
        )}

        {errors && "pedagogicalYearInstitutionProgramDetailId" in errors ? (
          <Col md={12}>
            <InfoPanel
              message={t("validations.institutionProgram")}
              color="danger"
              className={styles.mainInfoMessage}
              fullWidth
            />
          </Col>
        ) : null}

        {errors && "canPickedOnFirstPlace" in errors ? (
          <Col md={12}>
            <InfoPanel
              message={t("validations.institutionProgramFirstPickError")}
              color="danger"
              className={styles.mainInfoMessage}
              fullWidth
            />
          </Col>
        ) : null}
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="mediumBold">
            {t("institutionProgramDataStep.header")}
          </Typography>
        </Col>
      </Row>

      <Row className={styles.rowHeadingSpace}>
        {isGroup && (
          <Col md={6}>
            <Select
              label={t("institutionFilter.kindergartenInstitution")}
              name={nameof<typeof filters>("parentIntitutionId")}
              value={filters.parentIntitutionId}
              onChange={(e) => handleChange(e, setFilters)}
              emptyOption={{ value: undefined, text: "" }}
              selectData={
                {
                  valueProp: "id",
                  textProp: "name",
                  data: groupInstitutions || [],
                } as SelectData<CodeBook>
              }
            />
          </Col>
        )}
        <Col md={isGroup ? 6 : 4}>
          <Select
            label={t("common.programAge")}
            name={nameof<typeof filters>("programAgeId")}
            value={filters.programAgeId}
            onChange={(e) => handleChange(e, setFilters)}
            emptyOption={{ value: undefined, text: "" }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: programAges || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={isGroup ? 6 : 4}>
          <Select
            label={t("common.programDuration")}
            name={nameof<typeof filters>("programDurationId")}
            value={filters.programDurationId}
            onChange={(e) => handleChange(e, setFilters)}
            emptyOption={{ value: undefined, text: "" }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: programDurations || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
        <Col md={isGroup ? 6 : 4}>
          <Select
            label={t("common.program")}
            name={nameof<typeof filters>("programId")}
            value={filters.programId}
            onChange={(e) => handleChange(e, setFilters)}
            emptyOption={{ value: undefined, text: "" }}
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: programs || [],
              } as SelectData<CodeBook>
            }
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={3}>
          <ListPanel
            title={t("institutionProgramDataStep.institutionSelection")}
            className={styles.listPanel}
          >
            {getInstitutions()}
          </ListPanel>
        </Col>
        <Col xs={12} md={4}>
          <ListPanel title={t("institutionProgramDataStep.programSelection")}>
            {getPrograms()}
          </ListPanel>
        </Col>
        <Col xs={12} md={5}>
          <div className={styles.selectedProgramsContent}>
            <Row>
              <Col md={12}>
                <Typography
                  variant="smallHeading"
                  className={styles.selectedProgramsHeading}
                >
                  {`${t("institutionProgramDataStep.selectedPrograms")} (${
                    institutionPrograms.length
                  }/${maxPrograms})`}
                </Typography>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <InfoPanel
                  message={t(
                    "institutionProgramDataStep.selectedProgramsInfoMessage"
                  )}
                  color="success"
                  className={styles.mainInfoMessage}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                {institutionPrograms.length > 0 && (
                  <CardList
                    isNumbered={true}
                    isDraggable={true}
                    isCloseable={true}
                    onDragEnd={handleDrag}
                    onClose={handleClose}
                  >
                    {getSelectedPrograms()}
                  </CardList>
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </>
  );
}
